import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../App';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, MessageCircle, DollarSign, Heart, ShieldCheck, Crown, Ban, UserPlus, Activity, Star, MessageSquare, Shield, Smile, Sparkles, Lightbulb } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, sub, color = '#8B5CF6' }) => (
  <Card className="card-surface border-[#27272A]">
    <CardContent className="p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-6 h-6" style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Outfit' }}>{value}</p>
        <p className="text-sm text-[#A1A1AA]">{label}</p>
        {sub && <p className="text-xs text-[#71717A] mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const UserRow = ({ user }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#1A1A1A] last:border-0">
    <div>
      <p className="text-sm text-[#F9FAFB]">{user.name || 'No name'}</p>
      <p className="text-xs text-[#71717A]">{user.email}</p>
    </div>
    <div className="text-right">
      <span className={`text-xs px-2 py-0.5 rounded-full ${user.is_verified ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#71717A]/20 text-[#71717A]'}`}>
        {user.is_verified ? 'Verified' : 'Unverified'}
      </span>
      <p className="text-xs text-[#71717A] mt-1">{user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}</p>
    </div>
  </div>
);

const PaymentRow = ({ payment }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#1A1A1A] last:border-0">
    <div>
      <p className="text-sm text-[#F9FAFB] capitalize">{payment.type}</p>
      <p className="text-xs text-[#71717A]">{payment.user_email || 'Anonymous'}</p>
    </div>
    <div className="text-right">
      <p className="text-sm font-semibold text-[#D4AF37]">${payment.amount?.toFixed(2)}</p>
      <span className={`text-xs px-2 py-0.5 rounded-full ${payment.payment_status === 'paid' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#D4AF37]/20 text-[#D4AF37]'}`}>
        {payment.payment_status}
      </span>
    </div>
  </div>
);

const REACTION_TILES = [
  { key: 'acknowledged', label: 'Acknowledged', icon: Sparkles, color: '#D4AF37' },
  { key: 'reframed', label: 'Reframed', icon: Lightbulb, color: '#8B5CF6' },
  { key: 'felt_seen', label: 'Felt seen', icon: Heart, color: '#EC4899' },
  { key: 'felt_safer', label: 'Felt safer', icon: ShieldCheck, color: '#10B981' },
];

const ReactionsSnapshot = ({ data }) => {
  const total = data?.total || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <Card className="card-surface border-[#27272A]" data-testid="reactions-snapshot-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <Sparkles className="w-5 h-5 text-[#D4AF37]" />
          Today's "What Helped" Snapshot
          <span className="ml-auto text-xs text-[#71717A] font-normal">{total} reaction{total === 1 ? '' : 's'}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="text-[#71717A] text-sm">No reactions yet today.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="reactions-snapshot-grid">
            {REACTION_TILES.map((r) => {
              const Icon = r.icon;
              const count = data?.[r.key] || 0;
              return (
                <div
                  key={r.key}
                  className="rounded-xl border border-[#27272A] bg-[#0A0A0A] p-4 text-center"
                  data-testid={`reactions-snapshot-${r.key}`}
                >
                  <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: r.color }} />
                  <div className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Outfit' }}>{count}</div>
                  <div className="text-xs text-[#A1A1AA]">{r.label} ({pct(count)}%)</div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MoodSnapshot = ({ data }) => {
  const total = data?.total || 0;
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  return (
    <Card className="card-surface border-[#27272A]" data-testid="mood-snapshot-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
          <Smile className="w-5 h-5 text-[#D4AF37]" />
          Today's Mood Snapshot
          <span className="ml-auto text-xs text-[#71717A] font-normal">{total} check-in{total === 1 ? '' : 's'}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="text-[#71717A] text-sm">No mood check-ins yet today.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3" data-testid="mood-snapshot-grid">
            <div className="rounded-xl border border-[#27272A] bg-[#0A0A0A] p-4 text-center" data-testid="mood-snapshot-not-great">
              <div className="text-3xl mb-1">😞</div>
              <div className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Outfit' }}>{data.not_great}</div>
              <div className="text-xs text-[#A1A1AA]">Not great ({pct(data.not_great)}%)</div>
            </div>
            <div className="rounded-xl border border-[#27272A] bg-[#0A0A0A] p-4 text-center" data-testid="mood-snapshot-mixed">
              <div className="text-3xl mb-1">😐</div>
              <div className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Outfit' }}>{data.mixed}</div>
              <div className="text-xs text-[#A1A1AA]">Mixed ({pct(data.mixed)}%)</div>
            </div>
            <div className="rounded-xl border border-[#27272A] bg-[#0A0A0A] p-4 text-center" data-testid="mood-snapshot-okay">
              <div className="text-3xl mb-1">🙂</div>
              <div className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Outfit' }}>{data.okay}</div>
              <div className="text-xs text-[#A1A1AA]">Okay ({pct(data.okay)}%)</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RecentSignups = ({ users }) => (
  <Card className="card-surface border-[#27272A]">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <UserPlus className="w-5 h-5 text-[#8B5CF6]" />
        Recent Signups
      </CardTitle>
    </CardHeader>
    <CardContent>
      {users.length === 0 ? (
        <p className="text-[#71717A] text-sm">No users yet</p>
      ) : (
        <div className="space-y-3">
          {users.map((u) => <UserRow key={u.email} user={u} />)}
        </div>
      )}
    </CardContent>
  </Card>
);

const RecentPayments = ({ payments }) => (
  <Card className="card-surface border-[#27272A]">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <DollarSign className="w-5 h-5 text-[#D4AF37]" />
        Recent Payments
      </CardTitle>
    </CardHeader>
    <CardContent>
      {payments.length === 0 ? (
        <p className="text-[#71717A] text-sm">No payments yet</p>
      ) : (
        <div className="space-y-3">
          {payments.map((p) => <PaymentRow key={p.paypal_payment_id || `${p.type}-${p.created_at}`} payment={p} />)}
        </div>
      )}
    </CardContent>
  </Card>
);

const FeedbackRow = ({ item }) => (
  <div className="flex items-start justify-between py-3 border-b border-[#1A1A1A] last:border-0 gap-3">
    <div className="flex-1 min-w-0">
      <p className="text-sm text-[#F9FAFB] truncate">{item.title || 'Untitled'}</p>
      {item.feedback && <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{item.feedback}</p>}
      <p className="text-xs text-[#71717A] mt-1">{item.user_email}</p>
    </div>
    <div className="text-right shrink-0">
      {item.rating > 0 && (
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className={`w-3 h-3 ${s <= item.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#3F3F46]'}`} />
          ))}
        </div>
      )}
      <p className="text-xs text-[#71717A] mt-1">{item.closed_at ? new Date(item.closed_at).toLocaleDateString() : ''}</p>
    </div>
  </div>
);

const RecentFeedback = ({ feedback }) => (
  <Card className="card-surface border-[#27272A]">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <MessageSquare className="w-5 h-5 text-[#10B981]" />
        User Feedback
      </CardTitle>
    </CardHeader>
    <CardContent>
      {(!feedback || feedback.length === 0) ? (
        <p className="text-[#71717A] text-sm">No feedback yet</p>
      ) : (
        <div className="space-y-1">
          {feedback.map((f) => <FeedbackRow key={f.conversation_id} item={f} />)}
        </div>
      )}
    </CardContent>
  </Card>
);

const eventColors = {
  moderation: 'text-[#A1A1AA]',
  crisis_override: 'text-[#EF4444]',
  jailbreak_attempt: 'text-[#F59E0B]',
  user_banned: 'text-[#EF4444]',
  account_deleted: 'text-[#71717A]',
};

const AuditLog = ({ logs }) => (
  <Card className="card-surface border-[#27272A]">
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <Shield className="w-5 h-5 text-[#F59E0B]" />
        Audit Log (Safety Records)
      </CardTitle>
    </CardHeader>
    <CardContent>
      {(!logs || logs.length === 0) ? (
        <p className="text-[#71717A] text-sm">No audit events yet</p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {logs.map((log) => (
            <div key={`${log.event_type}-${log.timestamp}`} className="py-2 border-b border-[#1A1A1A] last:border-0">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-xs font-mono font-bold uppercase ${eventColors[log.event_type] || 'text-[#A1A1AA]'}`}>{log.event_type}</span>
                <span className="text-xs text-[#71717A]">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
              </div>
              <p className="text-xs text-[#A1A1AA] truncate">{log.user_email}</p>
              {log.details?.crisis_level && <p className="text-xs text-[#EF4444]">Crisis: {log.details.crisis_level}</p>}
              {log.details?.reason && <p className="text-xs text-[#71717A] truncate">{log.details.reason}</p>}
              {log.details?.text_preview && <p className="text-xs text-[#3F3F46] truncate italic">"{log.details.text_preview}"</p>}
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const HTTP_FORBIDDEN = 403;

const AdminPage = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [mood, setMood] = useState(null);
  const [reactions, setReactions] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, moodRes, reactionsRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/admin/mood-today').catch(() => ({ data: { not_great: 0, mixed: 0, okay: 0, total: 0 } })),
        api.get('/admin/reactions-today').catch(() => ({ data: { acknowledged: 0, reframed: 0, felt_seen: 0, felt_safer: 0, total: 0 } })),
      ]);
      setStats(statsRes.data);
      setMood(moodRes.data);
      setReactions(reactionsRes.data);
      setError(null);
    } catch (err) {
      setError(err.response?.status === HTTP_FORBIDDEN ? 'unauthorized' : 'failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#A1A1AA]">Loading dashboard...</p>
      </div>
    );
  }

  if (error === 'unauthorized') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <Card className="card-surface border-[#27272A] max-w-md w-full">
          <CardContent className="py-12 text-center">
            <Ban className="w-16 h-16 text-[#EF4444] mx-auto mb-4" />
            <h2 className="text-xl font-light text-[#F9FAFB]" style={{ fontFamily: 'Outfit' }}>Access Denied</h2>
            <p className="text-[#A1A1AA] mt-2">You don't have admin access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <p className="text-[#EF4444]">Failed to load stats. Try refreshing.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8 px-4" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tighter" style={{ fontFamily: 'Outfit' }}>
            Admin <span className="text-[#8B5CF6]">Dashboard</span>
          </h1>
          <p className="text-[#A1A1AA] mt-1">Welcome back, {user?.name || user?.email}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Heart} label="People Helped" value={stats.helped.total_responses} sub={`${stats.helped.today} today`} color="#10B981" />
          <StatCard icon={Users} label="Total Users" value={stats.users.total} sub={`${stats.users.new_today} new today`} color="#8B5CF6" />
          <StatCard icon={MessageCircle} label="Conversations" value={stats.conversations.total} sub={`${stats.conversations.active} active`} color="#3B82F6" />
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats.payments.total_revenue}`} sub={`${stats.payments.completed} payments`} color="#D4AF37" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={ShieldCheck} label="Verified Users" value={stats.users.verified} color="#10B981" />
          <StatCard icon={Crown} label="Premium Active" value={stats.users.premium_active} color="#D4AF37" />
          <StatCard icon={Activity} label="Premium Purchases" value={stats.payments.premium_purchases} color="#8B5CF6" />
          <StatCard icon={Heart} label="Donations" value={stats.payments.donations} color="#EC4899" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RecentSignups users={stats.recent_users} />
          <RecentPayments payments={stats.recent_payments} />
        </div>

        <MoodSnapshot data={mood} />
        <ReactionsSnapshot data={reactions} />

        <RecentFeedback feedback={stats.recent_feedback} />
        <AuditLog logs={stats.recent_audit} />
      </div>
    </div>
  );
};

export default AdminPage;
